import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { toast } from '../../components/ui/use-toast';

const ChangePasswordSection: React.FC = () => {
  const { t } = useTranslation('profile');
  const [form, setForm] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.current_password) {
      newErrors.current_password = t('current_password_required');
    }
    if (!form.password) {
      newErrors.password = t('new_password_required');
    } else if (form.password.length < 8) {
      newErrors.password = t('password_min_length');
    }
    if (form.password !== form.password_confirmation) {
      newErrors.password_confirmation = t('passwords_do_not_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setErrors({});
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/profile/change-password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        if (data.errors) {
          const fieldErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            fieldErrors[key] = Array.isArray(val) ? val[0] : String(val);
          });
          setErrors(fieldErrors);
        }
        throw new Error(data.message || 'Failed to change password');
      }

      setForm({ current_password: '', password: '', password_confirmation: '' });
      toast({ title: 'Password changed', description: 'Your password has been updated successfully.', variant: 'success' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to change password', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="bg-slate-50 border-b">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <ShieldCheck className="h-5 w-5" /> {t('change_password')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-6 max-w-md">
        <div className="space-y-1.5">
          <Label htmlFor="current_password" className="flex items-center gap-1.5 text-gray-600">
            <Lock className="h-3.5 w-3.5 text-slate-400" /> {t('current_password')}
          </Label>
          <Input
            id="current_password"
            type="password"
            value={form.current_password}
            onChange={e => handleChange('current_password', e.target.value)}
          />
          {errors.current_password && (
            <p className="text-sm text-destructive">{errors.current_password}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="new_password" className="flex items-center gap-1.5 text-gray-600">
            <KeyRound className="h-3.5 w-3.5 text-slate-400" /> {t('new_password')}
          </Label>
          <Input
            id="new_password"
            type="password"
            value={form.password}
            onChange={e => handleChange('password', e.target.value)}
          />
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirm_password" className="flex items-center gap-1.5 text-gray-600">
            <KeyRound className="h-3.5 w-3.5 text-slate-400" /> {t('confirm_password')}
          </Label>
          <Input
            id="confirm_password"
            type="password"
            value={form.password_confirmation}
            onChange={e => handleChange('password_confirmation', e.target.value)}
          />
          {errors.password_confirmation && (
            <p className="text-sm text-destructive">{errors.password_confirmation}</p>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSubmit} disabled={saving} >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            {t('change_password')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordSection;
